import { z } from 'zod'
import { UTCMillisecondsSchema } from './time'
import { LatLngSchema } from './locations/latLng'
import { TinyUserSchema, type TinyUser } from './entity'

export const WeekdaySchema = z.enum(['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'])
export const CapitalizeWeekdaySchema = z.enum(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'])
export type Weekday = z.infer<typeof WeekdaySchema>

export const TimeRangeSchema = z.object({
  startTime: UTCMillisecondsSchema,
  duration: z.number().describe('Duration in minutes'),
})
export type TimeRange = z.infer<typeof TimeRangeSchema>

export const CloserSettingsSchema = z.object({
  order: z.number().optional().meta({
    description: 'Ranking of the closer, used to sort closers when displaying timeslots',
  }),
  taskLockout: z.boolean().optional().meta({
    description: 'Decorated field from preferences',
  }),
  bookingPercent: z.number().optional().meta({
    description: 'Decorated field from preferences',
  }),
  meetingDuration: z.number().optional().meta({
    description: 'Length of the meeting in minutes',
    example: 60,
  }),
  timeBetweenAppointments: z.number().optional().meta({
    description: 'Buffer time between appointments in minutes',
    example: 30,
  }),
  serviceCenter: LatLngSchema.optional().meta({
    description: "The center of the closer's serviceable area",
    example: { lat: 37.7749, lng: -122.4194 },
  }),
  serviceRadius: z.number().optional().meta({
    description: 'Farthest distance the closer is willing to travel in meters',
    example: 50000,
  }),
  minimumNotice: z.number().optional().meta({
    description: 'Minimum notice time before appointment in hours',
    example: 24,
  }),
  maximumNotice: z.number().optional().meta({
    description: 'Maximum notice time before appointment in days',
    example: 14,
    default: 14,
  }),
  timeslots: z
    .record(WeekdaySchema, z.array(z.number()))
    .optional()
    .meta({
      description: 'Available time slots organized by day',
      example: { mon: [540, 600, 660], tue: [480, 540, 600] },
    }),
  excludes: z
    .array(z.number())
    .optional()
    .meta({
      description: 'Time slots to exclude (minutes from midnight)',
      example: [720, 780, 840],
    }),
  includes: z
    .array(z.number())
    .optional()
    .meta({
      description: 'Time slots to include (minutes from midnight)',
      example: [540, 600, 660, 720],
    }),
  lastUpdatedDate: UTCMillisecondsSchema.optional().meta({
    description: 'When closer settings were last updated, in UTC epoch milliseconds',
    example: 1672531200000,
  }),
})
export type CloserSettings = z.infer<typeof CloserSettingsSchema>

export const EffectiveCloserServiceAreaSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('unrestricted').meta({
      description: 'The closer has no location restriction',
    }),
  }),
  z.object({
    type: z.literal('radius'),
    center: LatLngSchema.meta({
      description: "The effective center of the closer's service area",
    }),
    radiusMeters: z.number().positive().meta({
      description: "The effective radius of the closer's service area in meters",
      example: 50000,
    }),
  }),
  z.object({
    type: z.literal('unknown').meta({
      description: 'The effective service area could not be determined from the configured data',
    }),
  }),
])
export type EffectiveCloserServiceArea = z.infer<typeof EffectiveCloserServiceAreaSchema>

export const TinyCloserSchema = z.object({
  user: TinyUserSchema,
  timezone: z.string().meta({
    description: "Closer's effective IANA timezone",
    example: 'America/Los_Angeles',
  }),
  times: z.array(TimeRangeSchema),
  bookingPercent: z.number().meta({
    description: 'Effective booking capacity preference used to calculate this closer availability',
  }),
  serviceArea: EffectiveCloserServiceAreaSchema.meta({
    description: 'Resolved service-area policy for location checks against this availability snapshot',
  }),
})
export type TinyCloser = z.infer<typeof TinyCloserSchema>

export const SmallCloserSchema = z.object({
  ...TinyUserSchema.shape,
  ...CloserSettingsSchema.shape,
  timezone: z.string(),
})
export type SmallCloser = TinyUser &
  CloserSettings & {
    timezone: string
  }

export const CLOSER_STATUSES = ['None', 'Needs_Help', 'Can_Close', 'Can_Help', 'Closer_Only'] as const
export const CloserStatusSchema = z.enum(CLOSER_STATUSES).meta({ id: 'closerStatus' })
export type CloserStatus = (typeof CLOSER_STATUSES)[number]

export const CLOSER_INTERACTION_OPTIONS = ['Full', 'Partial', 'None'] as const
export const CloserInteractionSchema = z.enum(CLOSER_INTERACTION_OPTIONS)
export type CloserInteraction = (typeof CLOSER_INTERACTION_OPTIONS)[number]

/** timestamp in milliseconds */
export type Timestamp = number

export type Minutes = number

/** minutes from midnight, according to user timezone */
export type AvailableTimes = Record<Weekday, Minutes[]>

export const AvailableCloserSchema = z.object({
  ...TinyUserSchema.shape,
  inServiceArea: z.boolean().optional(),
  available: z.boolean(),
  locked: z
    .boolean()
    .optional()
    .meta({ description: 'the closer currently has open tasks that need to be completed before continuing on' }),
  unavailableReason: z.string().optional().meta({ description: 'Human-readable reason why the closer is unavailable' }),
})
export type AvailableCloser = z.infer<typeof AvailableCloserSchema>
